import React, { useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Button } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { TextField } from 'formik-material-ui';
import OriginalTextField from '@material-ui/core/TextField';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { fetchBracketLibraries, fetchUserBracketLibraries } from '../../reducers/bracketLibraries';
import LoadingIndicator from '../../components/LoadingIndicator';
import {
  closeCustomBracketSetForm,
  createCustomBracketSetAction,
  updateCustomBracketSetAction
} from '../../reducers/customBracketSets';
import hasPermission from '../../selectors/hasPermission';
import { fetchOrganizationUsers } from '../../reducers/organizationUsers';
import LibraryAssignment from './LibraryAssignment';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
  value: PropTypes.string.isRequired
};

export const Main = ({
  data,
  fetchLibraries,
  fetchingLibraries,
  fetchedLibraries,
  libraries,
  canAssignUsers,
  fetchUsers,
  fetchingUsers,
  fetchedUsers,
  users,
  close,
  create,
  update,
  saving,
  isManagement
}) => {
  const handleSave = values => {
    if (data.id) {
      update(data.id, values).then(close);
    } else {
      create(values).then(close);
    }
  };

  useEffect(() => {
    if (!fetchedLibraries && !fetchingLibraries) {
      fetchLibraries({ visibleOnly: !isManagement });
    }
  }, [fetchLibraries, fetchedLibraries, fetchingLibraries, isManagement]);

  useEffect(() => {
    if (canAssignUsers && !fetchedUsers && !fetchingUsers) {
      fetchUsers();
    }
  }, [canAssignUsers, fetchUsers, fetchedUsers, fetchingUsers]);

  if (!data) {
    return '';
  }

  const initialData = JSON.parse(JSON.stringify(data)); // deepcopy
  // validation rules
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Custom set name is required.')
      .min(3)
      .max(255),
    teeth: Yup.array()
      .of(
        Yup.object().shape({
          tooth_number: Yup.string().required(),
          bracket_library_id: Yup.number().required(),
          bracket_library_name: Yup.string().required(),
          height: Yup.number()
            .min(0.9)
            .max(9)
            .nullable()
        })
      )
      .min(1, 'Teeth field must have at least one item')
      .required('Teeth field is required'),
    assigned_user_ids: Yup.array()
      .of(Yup.number())
      .nullable()
  });

  return (
    <Formik
      initialValues={{ ...initialData }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={values => handleSave(values)}
    >
      {({ values, submitForm, setFieldValue, setFieldTouched, setValues, dirty, isValid }) => {
        const onToothUpdate = teethData => {
          if (JSON.stringify(values.teeth) === JSON.stringify(teethData)) {
            return;
          }
          setFieldValue('teeth', teethData);
          setFieldTouched('teeth', true, false);
        };

        const handleReset = () => setValues(JSON.parse(JSON.stringify(initialData)));

        const handleClearAll = () => setValues({ name: values.name, teeth: [] });

        return (
          <Form>
            <Dialog maxWidth="lg" open={data !== null} close={close}>
              <DialogTitle>Custom Bracket Set</DialogTitle>
              <DialogContent>
                {fetchingLibraries && <LoadingIndicator />}
                {!fetchingLibraries && (
                  <>
                    <Box display="flex" justifyContent="center">
                      <Box width={600} flexDirection="column" display="flex">
                        <Field
                          name="name"
                          required
                          placeholder="Custom Set Name"
                          min={3}
                          max={255}
                          fullWidth
                          label="Custom Set Name"
                          component={TextField}
                          variant="outlined"
                          size="small"
                          disabled={saving}
                          style={{ marginBottom: 8 }}
                        />
                        {!!data.notes && (
                          <Field
                            name="notes"
                            placeholder="Notes"
                            min={3}
                            minRows={3}
                            max={255}
                            fullWidth
                            label="Notes"
                            component={TextField}
                            multiline
                            variant="outlined"
                            size="small"
                            disabled={saving}
                            InputProps={{ readOnly: true, disabled: true }}
                            style={{ marginBottom: 8 }}
                          />
                        )}
                        {canAssignUsers && (
                          <Autocomplete
                            multiple
                            style={{ marginBottom: 8 }}
                            id="assigned-users"
                            name="assigned_user_ids"
                            options={users}
                            getOptionLabel={option => option.name}
                            value={users.filter(user =>
                              values.assigned_user_ids?.includes(user.id)
                            )}
                            onChange={(event, newValue) => {
                              setValues({
                                ...values,
                                assigned_user_ids: newValue.map(user => user.id)
                              });
                            }}
                            renderInput={params => (
                              <OriginalTextField
                                {...params}
                                size="small"
                                variant="outlined"
                                label="Assign Users"
                                placeholder="All users if left empty"
                                helperText="Leave empty to make this set available to everyone in the organization."
                              />
                            )}
                            disabled={saving}
                          />
                        )}
                      </Box>
                    </Box>
                    <LibraryAssignment
                      libraries={libraries}
                      teethData={values.teeth}
                      update={onToothUpdate}
                      saving={saving}
                    />
                  </>
                )}
              </DialogContent>
              <DialogActions style={{ marginTop: 8 }}>
                <Button onClick={close} color="secondary" variant="contained" disabled={saving}>
                  Cancel
                </Button>
                <Button
                  onClick={handleClearAll}
                  disabled={Object.keys(values.teeth).length === 0 || saving}
                  color="secondary"
                  variant="outlined"
                >
                  Clear All
                </Button>

                <Button disabled={!dirty || saving} onClick={handleReset} variant="contained">
                  Discard Changes
                </Button>
                <Button
                  disabled={!dirty || !isValid || saving}
                  color="primary"
                  type="submit"
                  variant="contained"
                  onClick={submitForm}
                >
                  {saving ? 'Saving' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};

Main.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object,
  fetchLibraries: PropTypes.func.isRequired,
  fetchingLibraries: PropTypes.bool.isRequired,
  fetchedLibraries: PropTypes.bool.isRequired,
  libraries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  canAssignUsers: PropTypes.bool.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  fetchingUsers: PropTypes.bool.isRequired,
  fetchedUsers: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  close: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  isManagement: PropTypes.bool
};

Main.defaultProps = {
  data: null,
  isManagement: false
};

export default connect(
  (state, ownProps) => {
    const { organizationId, userId } = ownProps;

    let librariesState = {};

    if (userId) {
      librariesState =
        (state.bracketLibraries.users[userId] &&
          state.bracketLibraries.users[userId][organizationId]) ||
        {};
    } else {
      librariesState = state.bracketLibraries.organizations[organizationId] || {};
    }

    const { customBracketSets } = state;
    const existingBrackets = [
      ...new Set(customBracketSets.form.data?.teeth.map(t => t.bracket_library_id) || [])
    ];
    const canAssignUsers = hasPermission(state, {
      permissions: ['custom-bracket-sets.manage', 'organization.custom-bracket-sets.create'],
      organizationId
    });
    const organizationUsers = state.organizationUsers.organizations[organizationId] || null;
    return {
      librariesInitialized: librariesState.initialized || false,
      fetchingLibraries: librariesState.fetching || false,
      fetchedLibraries: librariesState.fetched || false,
      canAssignUsers,
      fetchingUsers: organizationUsers?.fetching || false,
      fetchedUsers: organizationUsers?.users && typeof organizationUsers?.users === 'object',
      users: organizationUsers?.users
        ? organizationUsers.users.map(user => ({
            id: user.id,
            name: user.full_name
          }))
        : [],
      libraries:
        librariesState.items
          ?.filter(item => item.hidden_at === null || existingBrackets.includes(item.id))
          .map(({ id, label }) => ({
            id,
            name: label
          })) || [],
      saving: state.customBracketSets.form.saving,
      data: customBracketSets.form.data,
      isManagement: state.auth.isManagement
    };
  },
  (dispatch, ownProps) => {
    const { organizationId, userId } = ownProps;
    return {
      fetchLibraries: ({ visibleOnly = false } = {}) =>
        userId
          ? dispatch(fetchUserBracketLibraries(organizationId, userId))
          : dispatch(fetchBracketLibraries(organizationId, { visibleOnly })),
      create: data => dispatch(createCustomBracketSetAction(ownProps.organizationId, data)),
      update: (id, data) =>
        dispatch(updateCustomBracketSetAction(ownProps.organizationId, id, data)),
      close: () => dispatch(closeCustomBracketSetForm()),
      fetchUsers: () => dispatch(fetchOrganizationUsers(organizationId))
    };
  }
)(Main);
